import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';

interface TicTacToeProps {
  onStatsUpdate: (stats: { gamesPlayed: number; wins: number; draws: number }) => void;
}

type Player = 'X' | 'O' | null;
type Board = Player[];

const TicTacToe: React.FC<TicTacToeProps> = ({ onStatsUpdate }) => {
  const [board, setBoard] = useState<Board>(Array(9).fill(null));
  const [currentPlayer, setCurrentPlayer] = useState<'X' | 'O'>('X');
  const [winner, setWinner] = useState<Player>(null);
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'draw'>('playing');
  const [score, setScore] = useState({ X: 0, O: 0, draws: 0 });
  const [gamesPlayed, setGamesPlayed] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [showGameCompleteModal, setShowGameCompleteModal] = useState(false);

  // Wedding-themed symbols
  const symbols = {
    X: { emoji: '💕', name: 'Hearts', color: 'text-pink-600' },
    O: { emoji: '💍', name: 'Rings', color: 'text-blue-600' }
  };

  // Load stats from localStorage
  useEffect(() => {
    const savedStats = localStorage.getItem('ticTacToeStats');
    if (savedStats) {
      const stats = JSON.parse(savedStats);
      setScore(stats.score || { X: 0, O: 0, draws: 0 });
      setGamesPlayed(stats.gamesPlayed || 0);
    }
  }, []);

  // Save stats to localStorage
  const saveStats = useCallback((newScore: typeof score, newGamesPlayed: number) => {
    const stats = { score: newScore, gamesPlayed: newGamesPlayed };
    localStorage.setItem('ticTacToeStats', JSON.stringify(stats));
    onStatsUpdate({ 
      gamesPlayed: newGamesPlayed, 
      wins: newScore.X + newScore.O, 
      draws: newScore.draws 
    });
  }, [onStatsUpdate]);

  // Check for winner
  const checkWinner = (squares: Board): Player => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8], // Rows
      [0, 3, 6], [1, 4, 7], [2, 5, 8], // Columns
      [0, 4, 8], [2, 4, 6] // Diagonals
    ];

    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return null;
  };

  // Handle cell click
  const handleClick = (index: number) => {
    if (board[index] || winner || gameStatus !== 'playing') return;

    const newBoard = [...board];
    newBoard[index] = currentPlayer;
    setBoard(newBoard);

    const gameWinner = checkWinner(newBoard);
    if (gameWinner) {
      setWinner(gameWinner);
      setGameStatus('won');
      const newScore = { ...score, [gameWinner]: score[gameWinner] + 1 };
      setScore(newScore);
      const newGamesPlayed = gamesPlayed + 1;
      setGamesPlayed(newGamesPlayed);
      saveStats(newScore, newGamesPlayed);
      // Show game complete modal after a short delay
      setTimeout(() => {
        setShowGameCompleteModal(true);
      }, 1000);
    } else if (newBoard.every(cell => cell !== null)) {
      setGameStatus('draw');
      const newScore = { ...score, draws: score.draws + 1 };
      setScore(newScore);
      const newGamesPlayed = gamesPlayed + 1;
      setGamesPlayed(newGamesPlayed);
      saveStats(newScore, newGamesPlayed);
      // Show game complete modal after a short delay
      setTimeout(() => {
        setShowGameCompleteModal(true);
      }, 1000);
    } else {
      setCurrentPlayer(currentPlayer === 'X' ? 'O' : 'X');
    }
  };

  // Reset game
  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setCurrentPlayer('X');
    setWinner(null);
    setGameStatus('playing');
    setShowGameCompleteModal(false);
  };

  // Reset all stats
  const resetStats = () => {
    setScore({ X: 0, O: 0, draws: 0 });
    setGamesPlayed(0);
    saveStats({ X: 0, O: 0, draws: 0 }, 0);
  };

  // Get cell content
  const getCellContent = (cell: Player) => {
    if (!cell) return null;
    return (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.2 }}
        className={`text-4xl ${symbols[cell].color}`}
      >
        {symbols[cell].emoji}
      </motion.div>
    );
  };

  // Get game status message
  const getGameStatusMessage = () => {
    if (showGameCompleteModal) {
      return winner ? `🎉 ${symbols[winner].name} Wins!` : "🤝 It's a Draw!";
    } else if (winner) {
      return `🎉 ${symbols[winner].name} Wins!`;
    } else if (gameStatus === 'draw') {
      return "🤝 It's a Draw!";
    } else {
      return `${symbols[currentPlayer].name}'s Turn`;
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-xl">
        <h3 className="text-2xl font-bold text-slate-800 mb-2">Wedding Tic-Tac-Toe</h3>
        <p className="text-slate-600 mb-4">
          Play the classic game with a romantic twist! Hearts 💕 vs Rings 💍
        </p>
        
        {/* Game Status */}
        <div className="text-center mb-4">
          <div className="text-lg font-semibold text-slate-700 mb-2">
            {getGameStatusMessage()}
          </div>
          <div className="flex justify-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-2xl">💕</span>
              <span>Hearts: {score.X}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">💍</span>
              <span>Rings: {score.O}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🤝</span>
              <span>Draws: {score.draws}</span>
            </div>
          </div>
        </div>

        {/* Game Controls */}
        <div className="flex justify-center gap-3">
          <motion.button
            onClick={resetGame}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-rose-500 to-pink-600 text-white px-6 py-2 rounded-lg font-bold shadow-lg"
          >
            🔄 New Game
          </motion.button>
          <motion.button
            onClick={() => setShowStats(!showStats)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-slate-200 text-slate-700 px-6 py-2 rounded-lg font-bold shadow-lg"
          >
            📊 Stats
          </motion.button>
        </div>
      </div>

      {/* Game Board */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl mb-8">
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
          {board.map((cell, index) => (
            <motion.button
              key={index}
              onClick={() => handleClick(index)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={cell !== null || winner !== null || gameStatus !== 'playing' || showGameCompleteModal}
              className={`aspect-square bg-gradient-to-br from-rose-50 to-pink-100 rounded-xl border-2 border-rose-200 flex items-center justify-center text-4xl font-bold shadow-lg transition-all duration-200 ${
                cell 
                  ? 'cursor-default' 
                  : gameStatus === 'playing' 
                    ? 'hover:from-rose-100 hover:to-pink-200 hover:border-rose-300 cursor-pointer' 
                    : 'cursor-not-allowed opacity-50'
              }`}
            >
              {getCellContent(cell)}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Stats Modal */}
      {showStats && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowStats(false)}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">📊</div>
              <h4 className="text-2xl font-bold text-slate-800 mb-2">Game Statistics</h4>
              <p className="text-slate-600">Your wedding tic-tac-toe stats</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center p-4 bg-rose-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💕</span>
                  <span className="font-medium">Hearts Wins</span>
                </div>
                <span className="text-2xl font-bold text-pink-600">{score.X}</span>
              </div>
              
              <div className="flex justify-between items-center p-4 bg-blue-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💍</span>
                  <span className="font-medium">Rings Wins</span>
                </div>
                <span className="text-2xl font-bold text-blue-600">{score.O}</span>
              </div>
              
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🤝</span>
                  <span className="font-medium">Draws</span>
                </div>
                <span className="text-2xl font-bold text-gray-600">{score.draws}</span>
              </div>
              
              <div className="flex justify-between items-center p-4 bg-purple-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🎮</span>
                  <span className="font-medium">Games Played</span>
                </div>
                <span className="text-2xl font-bold text-purple-600">{gamesPlayed}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <motion.button
                onClick={resetStats}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 bg-red-500 text-white py-3 rounded-lg font-bold shadow-lg"
              >
                Reset Stats
              </motion.button>
              <motion.button
                onClick={() => setShowStats(false)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-lg font-bold shadow-lg"
              >
                Close
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Game Complete Modal */}
      {showGameCompleteModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowGameCompleteModal(false)}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-6xl mb-4">
              {winner ? '🎉' : '🤝'}
            </div>
            
            <h4 className="text-3xl font-bold text-slate-800 mb-4">
              {winner ? `${symbols[winner].name} Wins!` : "It's a Draw!"}
            </h4>
            
            <p className="text-lg text-slate-600 mb-6">
              {winner 
                ? `Congratulations! ${symbols[winner].name} got three in a row!`
                : "Great game! No one got three in a row this time."
              }
            </p>

            <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl p-4 mb-6">
              <div className="text-sm text-slate-600 mb-2">Current Score</div>
              <div className="flex justify-center gap-6 text-lg">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💕</span>
                  <span className="font-bold text-pink-600">{score.X}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💍</span>
                  <span className="font-bold text-blue-600">{score.O}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🤝</span>
                  <span className="font-bold text-gray-600">{score.draws}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <motion.button
                onClick={resetGame}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white py-3 rounded-lg font-bold shadow-lg"
              >
                🔄 Play Again
              </motion.button>
              <motion.button
                onClick={() => setShowGameCompleteModal(false)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 bg-slate-200 text-slate-700 py-3 rounded-lg font-bold shadow-lg"
              >
                Close
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Game Instructions */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
        <h4 className="text-lg font-bold text-slate-800 mb-3">How to Play</h4>
        <div className="space-y-2 text-sm text-slate-600">
          <p>• Click on any empty square to place your symbol</p>
          <p>• Get 3 in a row (horizontal, vertical, or diagonal) to win</p>
          <p>• Hearts 💕 vs Rings 💍 - take turns playing</p>
          <p>• Track your wins and see who's the champion!</p>
        </div>
      </div>
    </div>
  );
};

export default TicTacToe;
